import { getClient } from '@api/client';
import { graphql } from '@api/generated';
import {
  UserAvailabilityStatus,
  UserSetAvailabilityStatus,
} from '@api/generated/graphql';
import ServiceUser from './types/ServiceUser';

const getServiceUsersQuery = graphql(`
  query getServiceUsersQuery {
    users {
      items {
        id
        contactCenterUserId
        name
        email
        available
        status
        stats {
          totalSessions
          offeredSessions
          missedSessions
          avgCallTime
          loginAt
          lastActionAt
          lastHandledAt
          lastIdleAt
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`);

async function getUsers(): Promise<ServiceUser[]> {
  const { data, error } = await getClient().query({
    query: getServiceUsersQuery,
    fetchPolicy: 'network-only',
  });

  if (error) {
    throw new Error(error.message);
  }

  return (
    data?.users?.items?.map((user) => ({
      ...user,
      capacity: 1,
      availabilityState: '',
      status: userStatusToUserSetStatus(user.status),
      canChangeStatus: canChangeStatus(user.status),
    })) ?? []
  );
}

function userStatusToUserSetStatus(
  status: UserAvailabilityStatus,
): UserSetAvailabilityStatus {
  switch (status) {
    case UserAvailabilityStatus.CallOffer:
    case UserAvailabilityStatus.OnACall:
    case UserAvailabilityStatus.Available:
      return UserSetAvailabilityStatus.Available;
    case UserAvailabilityStatus.AwayFromDesk:
      return UserSetAvailabilityStatus.AwayFromDesk;
    case UserAvailabilityStatus.Break:
      return UserSetAvailabilityStatus.Break;
    case UserAvailabilityStatus.Offline:
      return UserSetAvailabilityStatus.Offline;
    case UserAvailabilityStatus.WrapUp:
      return UserSetAvailabilityStatus.ExtendedWrapUp;
    case UserAvailabilityStatus.ExtendedWrapUp:
      return UserSetAvailabilityStatus.ExtendedWrapUp;
    case UserAvailabilityStatus.Unavailable:
      return UserSetAvailabilityStatus.Unavailable;
  }
}

function canChangeStatus(status: UserAvailabilityStatus): boolean {
  if (
    status === UserAvailabilityStatus.OnACall ||
    status === UserAvailabilityStatus.CallOffer
  ) {
    return false;
  }
  return true;
}

export default getUsers;
